import { RenderTexture, Sprite } from "pixi.js";

import { GpuStroke } from "./stroke.js";

import { GpuFill } from "./fill.js";

import { GpuCompositor } from "./compositor.js";

import { createTarget } from "./targets.js";

import { snapshotGLState, restoreGLState, snapshotClearState, restoreClearState } from "./gl-state.js";

import { createProfiles, normalizeProfile } from "./profiles.js";

import { color, sameColor } from "./color.js";

import { customTip, loadImage, uploadTip } from "./assets.js";

import { geometryClasses, pathPlot, splinePoints } from "./geometry.js";

import { TAU, clamp, identity, multiply, inverse, apply, Random, hash, noise2, bounds, segments, hatchSegments } from "./math.js";

export const DEGREES = "degrees", RADIANS = "radians";

let activeBrush = null;

export function currentBrush() {
  if (!activeBrush || activeBrush._destroyed) throw new Error("Create a GPU PixiBrush before drawing.");
  return activeBrush;
}

const finite = (x, label) => {
  if (!Number.isFinite(x)) throw new TypeError(label + " must be finite.");
  return x;
};

const dimension = (x, label) => {
  finite(x, label);
  if (x <= 0) throw new RangeError(label + " must be positive.");
  return Math.max(1, Math.round(x));
};

const pathSteps = (distance, spacing, minimum = 1) => {
  const count = Math.max(minimum, Math.ceil(distance / spacing));
  if (!Number.isFinite(count) || count > 65536) throw new RangeError("A path supports at most 65536 segments.");
  return count;
};

const cloneStyle = s => ({
  stroke: {
    ...s.stroke
  },
  fill: {
    ...s.fill
  },
  wash: {
    ...s.wash
  },
  hatch: {
    ...s.hatch,
    options: {
      ...s.hatch.options
    }
  },
  mass: {
    ...s.mass,
    options: {
      ...s.mass.options
    }
  },
  field: {
    ...s.field
  },
  clip: s.clip
});

/**
 * GPU-first drawing engine. The only host-specific operations are importing a
 * Pixi texture's GL handle and exposing its Sprite. Brush, fill, paths, state,
 * random generators and rendering are independent of the old p5 runtime.
 */
export class PixiBrush {
  constructor(appOrRenderer, options = {}) {
    const app = appOrRenderer?.renderer ? appOrRenderer : options.app;
    const renderer = app?.renderer ?? appOrRenderer ?? options.renderer;
    const gl = renderer?.gl;
    if (!gl?.createVertexArray || !renderer.texture?.getGlSource) throw new Error('PixiBrush requires PixiJS v8 WebGL2. Use preference: "webgl".');
    if (options.origin && ![ "center", "top-left" ].includes(options.origin)) throw new TypeError("origin must be center or top-left.");
    const supplied = options.renderTexture;
    if (supplied && !(supplied instanceof RenderTexture)) throw new TypeError("renderTexture must be a Pixi RenderTexture.");
    this.width = dimension(options.width ?? supplied?.width ?? renderer.screen.width, "width");
    this.height = dimension(options.height ?? supplied?.height ?? renderer.screen.height, "height");
    this.resolution = finite(options.resolution ?? supplied?.source.resolution ?? renderer.resolution ?? 1, "resolution");
    if (this.resolution <= 0) throw new RangeError("resolution must be positive.");
    if (supplied && (supplied.width !== this.width || supplied.height !== this.height || supplied.source.resolution !== this.resolution)) throw new Error("Supplied texture dimensions must match brush options.");
    this.renderer = renderer;
    this.app = app;
    this.gl = gl;
    this.origin = options.origin ?? "center";
    this._ownsTexture = !supplied;
    this.texture = supplied ?? RenderTexture.create({
      width: this.width,
      height: this.height,
      resolution: this.resolution,
      antialias: false,
      dynamic: true
    });
    this.sprite = new Sprite(this.texture);
    this.displayObject = this.sprite;
    this.sprite.anchor.set(.5);
    this.sprite.position.set(options.x ?? this.width / 2, options.y ?? this.height / 2);
    this._angleMode = RADIANS;
    this._matrix = identity();
    this._stack = [];
    this._commands = [];
    this._profiles = createProfiles();
    this._tips = new Map;
    this._registrations = new Map;
    this._fields = new Map;
    this._pressureLUTs = new WeakMap;
    this._brushScale = 1;
    this._rng = new Random(options.seed ?? Math.random());
    this._noiseSeed = hash(options.seed ?? 1);
    this._style = {
      stroke: {
        active: false,
        name: "HB",
        color: color("#000"),
        weight: 1
      },
      fill: {
        active: false,
        color: color("#000"),
        opacity: 150 / 255,
        bleed: .07,
        texture: .8,
        border: .5,
        scatter: true,
        direction: "out",
        angle: null
      },
      wash: {
        active: false,
        color: color("#000"),
        opacity: 150 / 255
      },
      hatch: {
        active: false,
        spacing: 8,
        angle: 0,
        options: {},
        name: null,
        color: null,
        weight: 1
      },
      mass: {
        active: false,
        name: "HB",
        color: color("#000"),
        options: {}
      },
      field: {
        active: false,
        name: "curved",
        wiggle: 1
      },
      clip: null
    };
    Object.assign(this, geometryClasses(this));
    this._shapePath = null;
    this._strokePlot = null;
    this._destroyed = false;
    this._target = null;
    this._mask = null;
    this._strokeGPU = null;
    this._fillGPU = null;
    this._compositor = null;
    this.diagnostics = {
      backend: "webgl2-gpu",
      cpuGeneratedStamps: 0,
      cpuGrownPolygons: 0,
      queuedCommands: 0,
      flushes: 0,
      submittedSegments: 0
    };
    this.activate();
    (options.parent ?? (options.addToStage === false ? null : app?.stage))?.addChild(this.sprite);
  }
  activate() {
    this._check();
    activeBrush = this;
    return this;
  }
  _check() {
    if (this._destroyed || this.texture.destroyed || !this.texture.source || this.texture.source.destroyed) throw new Error("This PixiBrush or its texture has been destroyed.");
  }
  _transaction(callback, clearOnly = false) {
    const state = clearOnly ? snapshotClearState(this.gl) : snapshotGLState(this.gl);
    if (state.lost) throw new Error("The WebGL context is lost. Recreate the brush after restoration.");
    try {
      return callback();
    } finally {
      if (clearOnly) restoreClearState(this.gl, state); else restoreGLState(this.gl, state);
      this.renderer.resetState();
    }
  }
  _ensureGPU() {
    const gl = this.gl, w = Math.max(1, Math.round(this.width * this.resolution)), h = Math.max(1, Math.round(this.height * this.resolution));
    if (!this._target) this._target = {
      framebuffer: gl.createFramebuffer(),
      width: w,
      height: h
    };
    const source = this.renderer.texture.getGlSource(this.texture.source);
    this._target.colorTexture = source.texture;
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, this._target.framebuffer);
    gl.framebufferTexture2D(gl.DRAW_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, source.texture, 0);
    if (!this._mask) {
      if (gl.checkFramebufferStatus(gl.DRAW_FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) throw new Error("Could not attach Pixi render texture.");
      this._mask = createTarget(gl, w, h);
      this._strokeGPU = new GpuStroke(gl);
      this._fillGPU = new GpuFill(gl, w, h);
      this._compositor = new GpuCompositor(gl, w, h);
    }
    return this._target;
  }
  _clearMask() {
    const gl = this.gl;
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, this._mask.framebuffer);
    gl.disable(gl.RASTERIZER_DISCARD);
    gl.disable(gl.SCISSOR_TEST);
    gl.colorMask(true, true, true, true);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
  }
  _paint(commands, isBrush, colorValue, rect) {
    if (!commands.length) return;
    this._clearMask();
    if (isBrush) {
      let batch = [];
      const flush = () => {
        if (batch.length) {
          if (this._strokeGPU.drawBatch) this._strokeGPU.drawBatch(batch, this._mask); else for (const c of batch) this._strokeGPU.draw(c, this._mask);
          batch = [];
        }
      };
      for (const cmd of commands) {
        const last = batch.at(-1);
        if (last && (last.profile !== cmd.profile || last.tipTexture !== cmd.tipTexture || last.clip !== cmd.clip || last.pressureLUT !== cmd.pressureLUT)) flush();
        batch.push(cmd);
      }
      flush();
    } else {
      rect = null;
      for (const cmd of commands) {
        const b = this._fillGPU.draw(cmd, this._mask);
        if (b) rect = rect ? [ Math.min(rect[0], b.minX), Math.min(rect[1], b.minY), Math.max(rect[2], b.maxX), Math.max(rect[3], b.maxY) ] : [ b.minX, b.minY, b.maxX, b.maxY ];
      }
      if (!rect) return;
    }
    this._compositor.draw({
      target: this._target.framebuffer,
      mask: this._mask,
      color: colorValue.slice(0, 3),
      bounds: rect,
      width: this._target.width,
      height: this._target.height,
      isBrush: isBrush,
      isWash: !isBrush && commands[0].mode === "wash",
      maskFlipY: false
    });
  }
  render() {
    this._check();
    if (!this._commands.length) return this;
    const commands = this._commands;
    this._commands = [];
    this._transaction(() => {
      this._ensureGPU();
      const gl = this.gl;
      gl.disable(gl.RASTERIZER_DISCARD);
      gl.disable(gl.SAMPLE_ALPHA_TO_COVERAGE);
      gl.disable(gl.SAMPLE_COVERAGE);
      gl.disable(gl.DEPTH_TEST);
      gl.disable(gl.CULL_FACE);
      let group = [], groupColor = null, rect = null;
      const flush = () => {
        if (group.length) this._paint(group, true, groupColor, rect);
        group = [];
        groupColor = null;
        rect = null;
      };
      for (const command of commands) {
        if (command.kind === "fill") {
          flush();
          this._paint([ command ], false, command.color, command.bounds);
          continue;
        }
        if (groupColor && !sameColor(groupColor, command.color)) flush();
        groupColor = command.color;
        group.push(command);
        const b = command.bounds;
        rect = rect ? [ Math.min(rect[0], b[0]), Math.min(rect[1], b[1]), Math.max(rect[2], b[2]), Math.max(rect[3], b[3]) ] : b.slice();
      }
      flush();
      this.diagnostics.flushes++;
    });
    this.diagnostics.queuedCommands = 0;
    return this;
  }
  flush() {
    return this.render();
  }
  clear(...args) {
    this._check();
    this._commands.length = 0;
    this.diagnostics.queuedCommands = 0;
    const rgba = args.length ? color(...args) : [ 0, 0, 0, 0 ];
    const warm = !!this._mask;
    this._transaction(() => {
      const target = warm ? this._target : this._ensureGPU(), gl = this.gl;
      gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, target.framebuffer);
      gl.disable(gl.RASTERIZER_DISCARD);
      gl.disable(gl.SCISSOR_TEST);
      gl.colorMask(true, true, true, true);
      gl.clearColor(rgba[0] * rgba[3], rgba[1] * rgba[3], rgba[2] * rgba[3], rgba[3]);
      gl.clear(gl.COLOR_BUFFER_BIT);
    }, warm);
    return this;
  }
  draw(callback) {
    if (typeof callback !== "function") throw new TypeError("draw expects a function.");
    this.activate();
    try {
      const value = callback(this);
      if (value?.then) throw new TypeError("draw callback must be synchronous.");
    } finally {
      this.render();
    }
    return this;
  }
  _disposeGPU() {
    this._strokeGPU?.destroy();
    this._fillGPU?.destroy();
    this._compositor?.destroy();
    this._mask?.destroy();
    if (this._target) this.gl.deleteFramebuffer(this._target.framebuffer);
    this._target = this._mask = this._strokeGPU = this._fillGPU = this._compositor = null;
  }
  resize(width, height, resolution = this.resolution) {
    this._check();
    width = dimension(width, "width");
    height = dimension(height, "height");
    finite(resolution, "resolution");
    if (resolution <= 0) throw new RangeError("resolution must be positive.");
    this._commands = [];
    this._transaction(() => this._disposeGPU());
    this.width = width;
    this.height = height;
    this.resolution = resolution;
    this.texture.resize(width, height, resolution);
    this._matrix = identity();
    this._stack = [];
    this._shapePath = this._strokePlot = null;
    this.sprite.width = width;
    this.sprite.height = height;
    return this.clear();
  }
  destroy(options = {}) {
    if (this._destroyed) return;
    const lost = this.gl.isContextLost();
    try {
      if (!lost && !this.texture.destroyed && !this.texture.source?.destroyed) this.render();
    } finally {
      const release = () => {
        this._disposeGPU();
        for (const tip of this._tips.values()) if (tip.texture) this.gl.deleteTexture(tip.texture);
      };
      if (lost) release(); else this._transaction(release);
      this._tips.clear();
      this._registrations.clear();
      this._commands = [];
      this._stack = [];
      this.sprite.destroy();
      if ((this._ownsTexture || options.texture) && !this.texture.destroyed) this.texture.destroy(true);
      this._destroyed = true;
      if (activeBrush === this) activeBrush = null;
    }
  }
  _radians(a) {
    return this._angleMode === DEGREES ? a * Math.PI / 180 : a;
  }
  _fromRadians(a) {
    return this._angleMode === DEGREES ? a * 180 / Math.PI : a;
  }
  angleMode(mode) {
    if (mode !== RADIANS && mode !== DEGREES) throw new TypeError("Use radians or degrees.");
    this._angleMode = mode;
    return this;
  }
  getAngleMode() {
    return this._angleMode;
  }
  _screenMatrix() {
    return {
      ...this._matrix,
      x: this._matrix.x + (this.origin === "center" ? this.width / 2 : 0),
      y: this._matrix.y + (this.origin === "center" ? this.height / 2 : 0)
    };
  }
  _screenPoint(x, y) {
    return apply(this._screenMatrix(), x, y);
  }
  _physicalMatrix() {
    const m = this._screenMatrix(), r = this.resolution;
    return {
      a: m.a * r,
      b: m.b * r,
      c: m.c * r,
      d: m.d * r,
      e: m.x * r,
      f: m.y * r
    };
  }
  push() {
    this._stack.push({
      matrix: {
        ...this._matrix
      },
      style: cloneStyle(this._style),
      angleMode: this._angleMode
    });
    return this;
  }
  pop() {
    const saved = this._stack.pop();
    if (saved) {
      this._matrix = saved.matrix;
      this._style = saved.style;
      this._angleMode = saved.angleMode;
    }
    return this;
  }
  translate(x, y) {
    this._matrix = multiply(this._matrix, {
      ...identity(),
      x: finite(x, "x"),
      y: finite(y, "y")
    });
    return this;
  }
  rotate(angle) {
    const a = this._radians(finite(angle, "angle"));
    this._matrix = multiply(this._matrix, {
      a: Math.cos(a),
      b: Math.sin(a),
      c: -Math.sin(a),
      d: Math.cos(a),
      x: 0,
      y: 0
    });
    return this;
  }
  scale(x, y = x) {
    this._matrix = multiply(this._matrix, {
      ...identity(),
      a: finite(x, "scale x"),
      d: finite(y, "scale y")
    });
    return this;
  }
  _withStyle(callback) {
    const saved = cloneStyle(this._style);
    try {
      return callback();
    } finally {
      this._style = saved;
    }
  }
  seed(value) {
    this._rng.seed(value);
    return this;
  }
  noiseSeed(value) {
    this._noiseSeed = hash(value);
    return this;
  }
  random(a = 0, b = 1) {
    if (Array.isArray(a)) return a[Math.floor(this._rng.next() * a.length)];
    if (arguments.length === 1) return this._rng.range(0, a);
    return this._rng.range(a, b);
  }
  noise(x, y = 0) {
    return noise2(x, y, this._noiseSeed);
  }
  wRand(values, weights) {
    if (!Array.isArray(values) || !values.length || !Array.isArray(weights) && !ArrayBuffer.isView(weights) || weights.length !== values.length) throw new TypeError("wRand requires equally sized values and weights.");
    const sum = weights.reduce((a, b) => {
      finite(b, "weight");
      if (b < 0) throw new RangeError("Weights must be nonnegative.");
      return a + b;
    }, 0);
    if (!Number.isFinite(sum) || sum <= 0) throw new RangeError("Weights must have a positive finite sum.");
    let value = this.random(sum);
    for (let i = 0; i < values.length; i++) {
      value -= weights[i];
      if (value < 0) return values[i];
    }
    return values.at(-1);
  }
  box() {
    return [ ...this._profiles.keys() ];
  }
  getBrushParams(name) {
    const p = this._profiles.get(name);
    return p ? normalizeProfile(p) : null;
  }
  scaleBrushes(factor) {
    finite(factor, "brush scale");
    if (factor <= 0) throw new RangeError("brush scale must be positive.");
    this._brushScale *= factor;
    return this;
  }
  pick(name) {
    if (!this._profiles.has(name)) throw new Error("Unknown brush: " + name);
    this._style.stroke.name = name;
    this._style.stroke.active = true;
    return this;
  }
  stroke(...args) {
    this._style.stroke.color = color(...args);
    this._style.stroke.active = true;
    return this;
  }
  strokeWeight(weight) {
    finite(weight, "weight");
    if (weight <= 0) throw new RangeError("weight must be positive.");
    this._style.stroke.weight = weight;
    return this;
  }
  set(name, value, weight = 1) {
    return this.pick(name).stroke(value).strokeWeight(weight);
  }
  noStroke() {
    this._style.stroke.active = false;
    return this;
  }
  add(name, params) {
    this._check();
    if (typeof name !== "string" || !name.length) throw new TypeError("Brush name must be a nonempty string.");
    const profile = normalizeProfile(params), registration = {};
    this._registrations.set(name, registration);
    const commit = image => {
      this._check();
      if (this._registrations.get(name) !== registration) return this;
      const previous = this._tips.get(name);
      let texture;
      this.render();
      if (image || previous) this._transaction(() => {
        if (image) texture = uploadTip(this.gl, image);
        if (previous?.texture) this.gl.deleteTexture(previous.texture);
      });
      this._profiles.set(name, profile);
      if (image) this._tips.set(name, {
        image: image,
        texture: texture
      }); else this._tips.delete(name);
      return this;
    };
    if (profile.type === "custom") return commit(customTip(profile.tip, this._angleMode));
    if (profile.type === "image") {
      const src = profile.image?.src;
      if (!src) throw new TypeError("An image brush requires image.src.");
      return loadImage(src).then(commit);
    }
    return commit(null);
  }
  clip(region) {
    if (!Array.isArray(region) || region.length !== 4 || region.some(v => !Number.isFinite(v))) throw new TypeError("clip expects [x1,y1,x2,y2].");
    const m = inverse(this._screenMatrix());
    if (!m) throw new Error("Cannot clip with a singular transform.");
    this._style.clip = {
      inverse: m,
      bounds: region.slice()
    };
    return this;
  }
  noClip() {
    this._style.clip = null;
    return this;
  }
  _pressureLUT(profile) {
    if (profile.pressureLUT) return profile.pressureLUT;
    const p = profile.pressure, fn = typeof p === "function" ? p : typeof p?.curve === "function" ? p.curve : null;
    if (!fn) return null;
    const lut = new Float32Array(64), range = p?.min_max ?? [ 0, 1 ];
    for (let i = 0; i < lut.length; i++) {
      const value = finite(fn(i / (lut.length - 1)), "pressure callback result");
      lut[i] = Math.max(0, range[0] + clamp(value) * (range[1] - range[0]));
    }
    const previous = this._pressureLUTs.get(profile);
    if (previous && previous.every((value, i) => value === lut[i])) return previous;
    this._pressureLUTs.set(profile, lut);
    return lut;
  }
  _strokePath(points, closed = false, override = null) {
    const style = override ?? this._style.stroke;
    if (!style.active || points.length < 2) return;
    this._check();
    const packed = segments(points, closed);
    if (!packed.totalLength) return;
    const profile = this._profiles.get(style.name);
    if (!profile) throw new Error("Unknown brush: " + style.name);
    const tip = this._tips.get(style.name);
    if ([ "image", "custom" ].includes(profile.type) && !tip) throw new Error("Await image brush registration before drawing.");
    const matrix = this._screenMatrix(), r = this.resolution, lut = this._pressureLUT(profile);
    const p = profile.pressure, varied = lut && p?.variation && Object.values(p.variation).some(value => value > 0);
    const values = varied ? p.min_max ?? [ 0, 1 ] : lut ?? (Array.isArray(p) || ArrayBuffer.isView(p) ? p : typeof p === "number" ? [ p ] : p?.min_max ?? [ 1, 1 ]);
    let minimum = Infinity, maximum = 0, endpointMin = Infinity, endpointMax = 0;
    for (const value of values) {
      minimum = Math.min(minimum, value);
      maximum = Math.max(maximum, value);
    }
    for (const value of packed.pressures) {
      endpointMin = Math.min(endpointMin, value);
      endpointMax = Math.max(endpointMax, value);
    }
    minimum = Math.max(.05, minimum * endpointMin);
    maximum = Math.max(0, maximum * endpointMax);
    const sharp = profile.sharpness, normalLimit = 5.77;
    const scatter = profile.type === "spray" ? profile.scatter * (maximum + normalLimit / 3) * 1.1 : profile.type === "default" ? profile.scatter * (Math.abs(sharp) + Math.abs(1 - sharp) * normalLimit / minimum) * 1.05 : profile.scatter * Math.SQRT2;
    const diameter = profile.weight * (profile.type === "default" ? maximum * maximum * 1.15 : profile.type === "spray" ? 1.1 : maximum);
    const extent = (scatter + diameter / Math.SQRT2) * style.weight * this._brushScale * Math.max(Math.hypot(matrix.a, matrix.c), Math.hypot(matrix.b, matrix.d)) + 2;
    const rect = bounds([ points ], matrix, extent).map(v => v * r);
    const command = {
      kind: "stroke",
      ...packed,
      profile: profile,
      weight: style.weight * this._brushScale,
      color: style.color.slice(),
      alpha: style.color[3],
      transform: matrix,
      resolution: r,
      seed: Math.floor(this._rng.next() * 16777215),
      tipTexture: tip?.texture ?? null,
      pressureLUT: lut,
      clip: this._style.clip,
      bounds: rect
    };
    this._commands.push(command);
    this.diagnostics.submittedSegments += packed.segments.length / 6;
    this.diagnostics.queuedCommands = this._commands.length;
  }
  line(x1, y1, x2, y2) {
    for (const v of [ x1, y1, x2, y2 ]) finite(v, "line coordinate");
    this._strokePath([ [ x1, y1 ], [ x2, y2 ] ]);
    return this;
  }
  flowLine(x, y, length, dir) {
    for (const v of [ x, y, length, dir ]) finite(v, "flow line parameter");
    if (length <= 0) return this;
    const base = this._radians(dir);
    if (!this._style.field.active) return this.line(x, y, x + Math.cos(base) * length, y - Math.sin(base) * length);
    const count = pathSteps(length, 6), step = length / count, points = [ [ x, y ] ];
    for (let i = 0; i < count; i++) {
      const angle = base + this._fieldAngle(x, y);
      x += Math.cos(angle) * step;
      y -= Math.sin(angle) * step;
      points.push([ x, y ]);
    }
    this._strokePath(points);
    return this;
  }
  fill(...args) {
    const c = args.length >= 3 ? color(args[0], args[1], args[2]) : color(args[0]), opacity = args.length >= 3 ? args[3] ?? 150 : args[1] ?? 150;
    this._style.fill = {
      ...this._style.fill,
      active: true,
      color: c,
      opacity: clamp(finite(opacity, "fill opacity") / 255)
    };
    return this;
  }
  noFill() {
    this._style.fill.active = false;
    return this;
  }
  fillBleed(value, direction = "out", angle = null) {
    if (![ "in", "out", "both" ].includes(direction)) throw new TypeError("bleed direction must be in, out or both.");
    Object.assign(this._style.fill, {
      bleed: clamp(finite(value, "bleed")),
      direction: direction,
      angle: angle == null ? null : this._radians(finite(angle, "bleed angle"))
    });
    return this;
  }
  fillTexture(texture = .4, border = .4, scatter = true) {
    Object.assign(this._style.fill, {
      texture: clamp(finite(texture, "texture")),
      border: clamp(finite(border, "border")),
      scatter: !!scatter
    });
    return this;
  }
  wash(...args) {
    const opacity = args.length >= 3 ? args[3] ?? 150 : args[1] ?? 150;
    this._style.wash = {
      active: true,
      color: args.length >= 3 ? color(args[0], args[1], args[2]) : color(args[0]),
      opacity: clamp(finite(opacity, "wash opacity") / 255)
    };
    return this;
  }
  noWash() {
    this._style.wash.active = false;
    return this;
  }
  _fillPaths(paths, mode) {
    const state = this._style[mode];
    if (!state.active || !paths.some(p => p.length >= 3)) return;
    const matrix = this._physicalMatrix();
    const command = {
      kind: "fill",
      mode: mode === "wash" ? "wash" : "fill",
      paths: paths.map(p => p.map(v => v.slice())),
      ...state,
      opacity: state.opacity * state.color[3],
      color: state.color.slice(),
      transform: matrix,
      resolution: this.resolution,
      clip: this._style.clip,
      seed: Math.floor(this._rng.next() * 16777215)
    };
    if (state.angle != null) {
      const m = this._screenMatrix(), x = Math.cos(state.angle), y = Math.sin(state.angle);
      command.angle = Math.atan2(m.b * x + m.d * y, m.a * x + m.c * y);
    }
    this._commands.push(command);
    this.diagnostics.queuedCommands = this._commands.length;
  }
  hatch(spacing, angle, options = {}) {
    finite(spacing, "hatch spacing");
    finite(angle, "hatch angle");
    if (spacing <= 0) throw new RangeError("hatch spacing must be positive.");
    for (const key of [ "rand", "gradient" ]) if (options[key] !== undefined && options[key] !== false) finite(options[key], "hatch " + key);
    Object.assign(this._style.hatch, {
      active: true,
      spacing: spacing,
      angle: this._radians(angle),
      options: {
        ...options
      }
    });
    return this;
  }
  hatchStyle(name, value, weight = 1) {
    if (!this._profiles.has(name)) throw new Error("Unknown brush: " + name);
    finite(weight, "hatch weight");
    if (weight <= 0) throw new RangeError("hatch weight must be positive.");
    Object.assign(this._style.hatch, {
      name: name,
      color: color(value),
      weight: weight
    });
    return this;
  }
  noHatch() {
    this._style.hatch.active = false;
    return this;
  }
  hatchArray(polygons) {
    this._hatchPaths(polygons.flatMap(p => p instanceof this.Polygon ? [ p.a ] : Array.isArray(p.a) ? [ p.a ] : [ p ]));
    return this;
  }
  _hatchPaths(paths, override = null) {
    const h = override ?? this._style.hatch;
    if (!h.active) return;
    const gradient = 1 + clamp(h.options.gradient ?? 0) * .1;
    const lines = hatchSegments(paths, h.spacing, h.angle, gradient);
    const style = {
      ...this._style.stroke,
      active: true,
      name: h.name ?? this._style.stroke.name,
      color: h.color ?? this._style.stroke.color,
      weight: h.weight ?? this._style.stroke.weight
    };
    let continuous = [];
    for (let index = 0; index < lines.length; index++) {
      const line = lines[index];
      const jitter = (h.options.rand ?? 0) * h.spacing;
      if (jitter) {
        for (const p of line) {
          p[0] += this.random(-jitter, jitter);
          p[1] += this.random(-jitter, jitter);
        }
      }
      if (h.options.continuous) {
        if (index % 2) line.reverse();
        continuous.push(...line);
      } else this._strokePath(line, false, style);
    }
    if (continuous.length) this._strokePath(continuous, false, style);
  }
  mass(name, value, options = {}) {
    if (!this._profiles.has(name)) throw new Error("Unknown brush: " + name);
    for (const key of [ "precision", "strength", "gradient" ]) if (options[key] !== undefined) finite(options[key], "mass " + key);
    this._style.mass = {
      active: true,
      name: name,
      color: color(value),
      options: {
        ...options
      }
    };
    return this;
  }
  noMass() {
    this._style.mass.active = false;
    return this;
  }
  massArray(polygons) {
    this._massPaths(polygons.map(p => p.a ?? p));
    return this;
  }
  _massPaths(paths) {
    const m = this._style.mass;
    if (!m.active) return;
    const precision = clamp(m.options.precision ?? .6, .05, 1), strength = clamp(m.options.strength ?? .8, .05, 2);
    const profile = this._profiles.get(m.name), spacing = Math.max(.5, (profile.weight + profile.scatter * .35) * 4 / precision);
    for (let i = 0; i < 3; i++) this._hatchPaths(paths, {
      active: true,
      name: m.name,
      color: m.color,
      weight: strength * (i === 0 ? 1 : .7),
      spacing: spacing,
      angle: .35 + i * .12,
      options: {
        rand: .2,
        gradient: m.options.gradient ?? 0
      }
    });
    if (m.options.outline) for (const path of paths) this._strokePath(path, true, {
      active: true,
      name: m.name,
      color: m.color,
      weight: strength
    });
  }
  _shape(paths, closed = true) {
    this._fillPaths(paths, "wash");
    this._fillPaths(paths, "fill");
    this._massPaths(paths);
    this._hatchPaths(paths);
    for (const p of paths) this._strokePath(p, closed);
    return this;
  }
  polygon(points) {
    const polygon = new this.Polygon(points);
    polygon.show();
    return polygon;
  }
  rect(x, y, width, height, mode = "corner") {
    for (const v of [ x, y, width, height ]) finite(v, "rectangle parameter");
    if (![ "corner", "center" ].includes(mode)) throw new TypeError("rectangle mode must be corner or center.");
    if (mode === "center") {
      x -= width / 2;
      y -= height / 2;
    }
    return this.polygon([ [ x, y ], [ x + width, y ], [ x + width, y + height ], [ x, y + height ] ]);
  }
  circle(x, y, diameter) {
    for (const v of [ x, y, diameter ]) finite(v, "circle parameter");
    if (!(diameter > 0)) throw new RangeError("circle diameter must be positive.");
    const points = [], n = Math.max(24, Math.min(128, Math.ceil(Math.PI * diameter / 6)));
    for (let i = 0; i < n; i++) {
      const a = i / n * TAU;
      points.push([ x + Math.cos(a) * diameter / 2, y + Math.sin(a) * diameter / 2 ]);
    }
    return this.polygon(points);
  }
  arc(x, y, radius, start, end) {
    for (const v of [ x, y, radius, start, end ]) finite(v, "arc parameter");
    if (radius <= 0) throw new RangeError("arc radius must be positive.");
    const a = this._radians(start), b = this._radians(end), delta = b - a, n = pathSteps(Math.abs(delta) * radius, 5, 2), points = [];
    for (let i = 0; i <= n; i++) {
      const t = a + delta * i / n;
      points.push([ x + radius * Math.cos(t), y - radius * Math.sin(t) ]);
    }
    this._strokePath(points);
    return pathPlot(this, points);
  }
  spline(vertices, curvature = .5) {
    const points = splinePoints(vertices, curvature);
    this._strokePath(points);
    return pathPlot(this, points);
  }
  beginShape(curvature = 0) {
    this._shapePath = {
      vertices: [],
      curvature: curvature
    };
    return this;
  }
  vertex(x, y, pressure = 1) {
    if (!this._shapePath) throw new Error("Call beginShape before vertex.");
    this._shapePath.vertices.push([ finite(x, "x"), finite(y, "y"), finite(pressure, "pressure") ]);
    return this;
  }
  endShape(close = false) {
    if (!this._shapePath) throw new Error("Call beginShape before endShape.");
    const {vertices: vertices, curvature: curvature} = this._shapePath;
    if (vertices.length < 2) throw new Error("A shape requires at least two vertices.");
    this._shapePath = null;
    const points = splinePoints(vertices, curvature, close);
    this._shape([ points ], close);
    return pathPlot(this, points);
  }
  beginStroke(type, x, y) {
    this._strokePlot = new this.Plot(type);
    this._strokePlot.origin = [ x, y ];
    return this;
  }
  move(angle, length, pressure = 1) {
    if (!this._strokePlot) throw new Error("Call beginStroke before move.");
    this._strokePlot.addSegment(angle, length, pressure);
    return this;
  }
  endStroke(angle = 0, pressure = 1) {
    if (!this._strokePlot) throw new Error("Call beginStroke before endStroke.");
    const plot = this._strokePlot;
    this._strokePlot = null;
    plot.endPlot(angle, pressure).draw();
    return plot;
  }
  addField(name, generator, options = {}) {
    if (typeof name !== "string" || !name.length) throw new TypeError("Field name must be a nonempty string.");
    if (typeof generator !== "function") throw new TypeError("Field generator must be a function.");
    const mode = options.angleMode ?? "degrees";
    if (![ "degrees", "radians" ].includes(mode)) throw new TypeError("Field angle mode must be degrees or radians.");
    this._fields.set(name, {
      generator: generator,
      angleMode: mode,
      grid: null,
      time: 0
    });
    return this;
  }
  listFields() {
    return [ ...new Set([ "hand", "curved", "zigzag", "waves", "seabed", "spiral", "columns", ...this._fields.keys() ]) ];
  }
  field(name) {
    if (!this.listFields().includes(name)) throw new Error("Unknown field: " + name);
    this._style.field.active = true;
    this._style.field.name = name;
    const field = this._fields.get(name);
    if (field && !field.grid) this.refreshField();
    return this;
  }
  noField() {
    this._style.field.active = false;
    return this;
  }
  wiggle(value = 1) {
    this._style.field.wiggle = finite(value, "wiggle");
    return this;
  }
  refreshField(time = 0) {
    finite(time, "field time");
    if (!this._style.field.active) throw new Error("Activate a field before refreshing it.");
    const field = this._fields.get(this._style.field.name);
    if (!field) return this;
    const cols = Math.ceil(this.width / 16) + 3, rows = Math.ceil(this.height / 16) + 3, grid = Array.from({
      length: cols
    }, () => new Float32Array(rows));
    const result = field.generator(time, grid) ?? grid;
    if (!Array.isArray(result) || !result.length || !result.every(column => (Array.isArray(column) || ArrayBuffer.isView(column)) && column.length && Array.from(column).every(Number.isFinite))) throw new TypeError("Field generator must return nonempty columns of finite angles.");
    field.grid = result;
    field.time = time;
    return this;
  }
  _fieldAngle(x, y) {
    const state = this._style.field;
    if (!state.active) return 0;
    const field = this._fields.get(state.name);
    let angle = 0;
    if (field?.grid) {
      const p = this._screenPoint(x, y), c = clamp(Math.floor(p[0] / 16), 0, field.grid.length - 1), column = field.grid[c];
      const r = clamp(Math.floor(p[1] / 16), 0, column.length - 1);
      angle = column[r] * (field.angleMode === "radians" ? 1 : Math.PI / 180);
    } else {
      const n = this.noise(x * .006, y * .006);
      switch (state.name) {
       case "hand":
        angle = (n - .5) * .15;
        break;

       case "curved":
        angle = (n - .5) * 1.5;
        break;

       case "zigzag":
        angle = Math.sin(x * .015) > 0 ? .35 : -.35;
        break;

       case "waves":
       case "seabed":
        angle = Math.sin(x * .01 + y * .003) * .4;
        break;

       case "spiral":
        angle = Math.atan2(-y, x) + Math.PI / 2;
        break;

       case "columns":
        angle = Math.sin(y * .01) * .5;
        break;
      }
    }
    return angle * state.wiggle;
  }
}

export const createPixiBrush = (appOrRenderer, options) => new PixiBrush(appOrRenderer, options);
